import { useLazyQuickSearchQuery } from '@api/api';
import { LoadingStatus, QuickSearchResponse } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  ContentArea,
  GoToFullSearch,
  QuickSearchResults,
  SearchInput,
  SearchResultsHint,
} from '@components';
import { useAppDispatch, useAppSelector, useInternalRedirect } from '@hooks';
import {
  Box,
  ClickAwayListener,
  Collapse,
  Container,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { getSpecialPage } from '@store/app/app.slice';
import { getModalState, setModalState } from '@store/modal/modal.slice';
import {
  getSearchQuery,
  isInFullSearch,
  resetSearchOffsets,
  setProductsCategoryLink,
  setSearchQuery,
} from '@store/search/search.slice';
import { t } from '@translations';
import { updateURLQuery } from '@utils/query.utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';

// STYLES
//--------
const MegaMenuContentWrapper = styled(Container)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #f0f0f0;
  z-index: 1000;
  box-shadow: 0px 8px 8px rgb(0 0 0 / 20%);
  overflow: auto;
  max-height: calc(100vh - 132px);
`;
// TYPES
//-------
type HeaderSearchProps = {
  guidValue?: string;
  children?: React.ReactNode;
  variant: 'mobile' | 'desktop';
};

export const HeaderSearch = ({ guidValue = 'topBarSearch', variant }: HeaderSearchProps) => {
  // Search data - Local Storage
  // const [localStorageQuery, setLocalStorageQuery] = useLocalStorage('searchQuery', '');

  // Search Data - Redux
  const dispatch = useAppDispatch();
  const currentStoreQuery = useAppSelector(getSearchQuery);
  const inFullSearch = useAppSelector(isInFullSearch);

  // Search API call
  const [doSearch, response] = useLazyQuickSearchQuery();
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const debouncedQuery = useDebounce<string>(currentQuery, 500);
  const debouncedUpdateURLQuery = useDebounce<string>(currentQuery, 500);
  const { isVisible } = useAppSelector(getModalState(guidValue));
  const handler = (event: any) => {
    const query = event.target.value;
    setCurrentQuery(query);
  };

  // Watch debounced query and update store
  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      dispatch(resetSearchOffsets());
      dispatch(setProductsCategoryLink(''));
      dispatch(setSearchQuery(debouncedQuery));
      // setLocalStorageQuery(debouncedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);
  useEffect(() => {
    if (debouncedUpdateURLQuery.length >= 3 && inFullSearch) {
      updateURLQuery(debouncedUpdateURLQuery);
    } else if (debouncedUpdateURLQuery.length < 3 && inFullSearch) {
      updateURLQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUpdateURLQuery]);
  // Watch Redux search query string
  // Perform Search when Redux search is updated
  // Do not update Quick Search if Full Search Block is on Page
  useEffect(() => {
    if (currentStoreQuery.length >= 3 && !inFullSearch) {
      doSearch({ query: currentStoreQuery });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreQuery, inFullSearch]);

  const shouldShowResults = !inFullSearch && currentStoreQuery.length >= 3;
  const results = (response.currentData as QuickSearchResponse)?.data || {};
  const totalCount = Object.entries(results)
    .map(([_, result]) => result.totalCount)
    .reduce((accumulator = 0, currentValue = 0) => accumulator + currentValue, 0);

  // Navigate to full search on Enter
  const redirect = useInternalRedirect();
  const searchResultsUrl = useAppSelector(getSpecialPage('searchResultsPage')) || '';
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !inFullSearch) {
      redirect(searchResultsUrl);
      updateURLQuery(currentQuery);
    }
  };

  // Watch breakpoint change
  const theme = useTheme();
  const isMediumOrLargerScreen = useMediaQuery(theme.breakpoints.up('md'));

  const toggleBodyScroll = useCallback(
    (isBlocked: boolean) => {
      if (isBlocked && inFullSearch) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    },
    [inFullSearch]
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    const query = url.searchParams.get('query') || '';
    setCurrentQuery(currentStoreQuery || query);
    // setCurrentQuery(currentStoreQuery || localStorageQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediumOrLargerScreen]);

  useEffect(() => {
    toggleBodyScroll(isVisible);
    return () => {
      toggleBodyScroll(false);
    };
  }, [isVisible, toggleBodyScroll]);

  if (
    (variant === 'mobile' && isMediumOrLargerScreen) ||
    (variant === 'desktop' && !isMediumOrLargerScreen)
  ) {
    return null;
  }

  const setIsVisible = (newValue: boolean) =>
    dispatch(setModalState({ id: guidValue, isVisible: newValue, type: 'submenu' }));

  const content = inFullSearch ? null : (
    <Box className='search-megamenu'>
      <SearchResultsHint currentSearch={debouncedQuery} status={response as LoadingStatus} />
      <Collapse in={shouldShowResults}>
        <QuickSearchResults
          searchId={guidValue}
          data={results}
          status={response as LoadingStatus}
        />
      </Collapse>
      <GoToFullSearch totalCount={totalCount?.toString()} />
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <ClickAwayListener onClickAway={() => setIsVisible(false)}>
        <Box
          sx={{
            flexGrow: 1,
            maxWidth: {
              md: '384px',
            },
            marginInlineStart: 'auto',
          }}
        >
          <SearchInput
            placeholder={t(TextCodes.Search.NavSearchPlaceholder)}
            onChange={handler}
            value={currentQuery}
            onKeyDown={onKeyDown}
            onFocus={() => setIsVisible(true)}
          />
          <MegaMenuContentWrapper>
            <Collapse in={isVisible}>
              <Box
                className='inner-content'
                sx={{
                  maxHeight: 'inherit',
                  overflow: 'auto',
                }}
              >
                {content && <ContentArea content={content} />}
              </Box>
            </Collapse>
          </MegaMenuContentWrapper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};
